import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { globalModel } from "../global/GlobalModel";
import { formatApiDateTime } from "../utils/Backend";
import {
  type AccountDataScope,
  resolveAccountListFilter,
} from "./AccountDataScope";
import { StoreOrderApi } from "./StoreOrderApi";
import type { AppMyStoreOrderRow, ListMyStoreOrdersResult } from "./StoreOrderType";
import { resolveStoreOrderDetailPath, isStoreOrderPaid } from "./storeOrderDetailUtils";
import { accountTablePagination } from "./accountTableConfig";
import "./MyOrdersPage.css";

type Props = {
  scope: AccountDataScope;
};

/** 各列 width 之和，表格超出容器时出现横向滚动 */
const ORDERS_TABLE_SCROLL_X = 1060;

export const MyOrdersPanel = observer((props: Props) => {
  const { scope } = props;
  const navigate = useNavigate();
  const listFilter = useMemo(
    () => resolveAccountListFilter(scope),
    [scope]
  );
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AppMyStoreOrderRow[]>([]);

  const load = useCallback(async () => {
    if (!globalModel.isLoggedIn) return;
    setLoading(true);
    const res = await StoreOrderApi.listMyOrders({
      ...listFilter,
      pageNum: 1,
      pageSize: 100,
    });
    setLoading(false);
    if (typeof res === "string") {
      return;
    }
    const data = res as ListMyStoreOrdersResult;
    setRows(data.list ?? []);
  }, [listFilter]);

  useEffect(() => {
    if (!globalModel.isLoggedIn) {
      setRows([]);
      return;
    }
    void load();
  }, [load, globalModel.isLoggedIn]);

  const openDetail = useCallback(
    (row: AppMyStoreOrderRow) => {
      navigate(resolveStoreOrderDetailPath(row, scope));
    },
    [navigate, scope]
  );

  const columns: ColumnsType<AppMyStoreOrderRow> = [
    {
      title: "产品名称",
      dataIndex: "productName",
      key: "productName",
      ellipsis: true,
      width: 140,
    },
    {
      title: "所属空间",
      dataIndex: "spaceName",
      key: "spaceName",
      ellipsis: true,
      width: 120,
    },
    {
      title: "购买规格",
      dataIndex: "serviceTierLabel",
      key: "serviceTierLabel",
      width: 100,
    },
    {
      title: "状态",
      dataIndex: "displayStatusLabel",
      key: "displayStatusLabel",
      width: 88,
    },
    {
      title: "订单号",
      dataIndex: "orderId",
      key: "orderId",
      width: 180,
      ellipsis: true,
      render: (v: string) => <span className="my-orders-order-id">{v}</span>,
    },
    {
      title: "实付金额",
      key: "pay",
      width: 96,
      render: (_: unknown, r) =>
        r.displayStatus === "unpaid"
          ? `¥${r.productPriceYuan.toFixed(2)}`
          : `¥${r.payAmountYuan.toFixed(2)}`,
    },
    {
      title: "下单时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 168,
      render: (v) => formatApiDateTime(v),
    },
    {
      title: "支付时间",
      dataIndex: "payTime",
      key: "payTime",
      width: 168,
      render: (v, r) => (isStoreOrderPaid(r) && v ? formatApiDateTime(v) : "—"),
    },
  ];

  if (!globalModel.isLoggedIn) {
    return <p className="my-orders-msg">请先登录后查看订单</p>;
  }

  return (
    <div className="my-orders-inner my-orders-inner--embedded">
      <Spin spinning={loading}>
        <Table
          rowKey="orderId"
          className="customer-account-table customer-account-table--clickable"
          columns={columns}
          dataSource={rows}
          scroll={{ x: ORDERS_TABLE_SCROLL_X }}
          pagination={accountTablePagination(rows.length)}
          onRow={(r) => ({
            onClick: () => openDetail(r),
          })}
        />
      </Spin>
    </div>
  );
});
